// ═══════════════════════════════════════════════════════════════
// ClearView CRM Settings Service — Supabase App Settings & Pathways
// ═══════════════════════════════════════════════════════════════
import { supabase } from "../lib/supabase";
import { debugLog } from "../utils/debugLog";

export const settingsService = {
  // ─── App Settings ───

  /**
   * Get a single setting by key
   */
  async get(key) {
    const { data, error } = await supabase
      .from("app_settings")
      .select("value")
      .eq("key", key)
      .single();

    if (error) {
      debugLog.warn("settingsService.get", `Key "${key}" not found`);
      return null;
    }
    return data.value;
  },

  /**
   * Set a single setting
   */
  async set(key, value) {
    const { error } = await supabase
      .from("app_settings")
      .upsert({ key, value, updated_at: new Date().toISOString() });

    if (error) {
      debugLog.error("settingsService.set", error);
      throw error;
    }
    debugLog.data("Setting saved", { key });
  },

  /**
   * Get all settings as a key-value object
   */
  async getAll() {
    const { data, error } = await supabase.from("app_settings").select("*");

    if (error) {
      debugLog.error("settingsService.getAll", error);
      return {};
    }
    const settings = {};
    (data || []).forEach((row) => {
      settings[row.key] = row.value;
    });
    debugLog.data("Settings loaded from Supabase");
    return settings;
  },

  // ─── Pathways ───

  /**
   * Get all pathways ordered by sort_order
   */
  async getPathways() {
    const { data, error } = await supabase
      .from("pathways")
      .select("*")
      .order("sort_order");

    if (error) {
      debugLog.error("settingsService.getPathways", error);
      throw error;
    }
    return data || [];
  },

  /**
   * Add a new pathway
   */
  async addPathway(pathway) {
    const { data, error } = await supabase
      .from("pathways")
      .insert({
        id: pathway.id,
        name: pathway.name,
        color: pathway.color,
        sort_order: pathway.sortOrder || 99,
      })
      .select()
      .single();

    if (error) {
      debugLog.error("settingsService.addPathway", error);
      throw error;
    }
    debugLog.action("Pathway added", { id: pathway.id });
    return data;
  },

  /**
   * Delete a pathway
   */
  async removePathway(pathwayId) {
    const { error } = await supabase
      .from("pathways")
      .delete()
      .eq("id", pathwayId);

    if (error) {
      debugLog.error("settingsService.removePathway", error);
      throw error;
    }
    debugLog.action("Pathway removed", { id: pathwayId });
  },
};
